/**
 * THT-675 — pure helpers for the fleet-wide insurance premium.
 *
 * The customer-level override lives on two Customer columns:
 *   - `insurance_premium_mode` (InsurancePremiumMode)
 *   - `fleet_insurance_premium_amount_cents` (INTEGER CENTS)
 *
 * Everything the customer sees or types is DOLLARS; cents exist only at the API
 * boundary (John, 2026-08-20 — integer cents avoid float drift). Per-asset
 * `insurance_payment_amount` stays DECIMAL DOLLARS, so the two are converted
 * here rather than at each read site.
 *
 * Kept dependency-free of React on purpose: consumed by the hook, the control,
 * the asset form, the asset overview, and the pending-truck card.
 */

import {
  Asset,
  Entity,
  InsurancePremiumMode,
  User,
} from '../../../../../../../types/apiTypes';
import {
  formatCentsToDollars,
  parseDollarsToCents,
} from '../../../../../../../utils/attributionUtils';

/** Shown wherever a per-asset insurance amount would otherwise appear. */
export const FLEET_POLICY_LABEL = 'Covered by fleet policy';

/**
 * Segmented-toggle copy (spec §3.4, renamed in §7/R2.1 — John, 2026-08-24).
 * The names are the modes as a customer says them out loud; the constant names
 * still describe the mode they belong to, so no import site had to move.
 */
export const PER_ASSET_OPTION_LABEL = 'Each Truck';
export const FLEET_OPTION_LABEL = 'Entire Fleet';
export const MODE_QUESTION_LABEL = 'How do you pay for insurance?';

/**
 * Spec §7/R2.2 — the standing description of Each Truck mode, shown whenever
 * that option is selected (not only while switching). It answers the question
 * the option itself raises: the amounts exist, so where are they edited?
 *
 * It reads as a continuation of the copy already on screen rather than a block
 * of its own, so the modal appends it to `perAssetSwitchWarning` when there is
 * a warning to append to (Enzo, 2026-08-24).
 */
export const PER_ASSET_OPTION_DESCRIPTION =
  'You can edit each amount individually by selecting the truck or trailer from the table.';
export const FLEET_AMOUNT_LABEL = 'Fleet Insurance Premium Per Month';

/**
 * Collapsed-summary copy (collapsed-UI spec §2 / §3.1). The control is a
 * set-once setting, so its steady state is one line above the asset table; the
 * mode words are the toggle's own labels so the summary and the editing UI can
 * never drift apart.
 */
export const INSURANCE_SUMMARY_LABEL = 'Insurance';
export const CHANGE_INSURANCE_LABEL = 'Change';

/** Title of the edit modal `Change` opens (collapsed-UI spec §3.2). */
export const FLEET_INSURANCE_MODAL_TITLE = 'Insurance settings';

/**
 * The per-asset amount a NEWLY CREATED asset carries in fleet mode. The trucks /
 * trailers / admin-users / bulk-asset joi schemas all keep
 * `insurance_payment_amount` required (spec §3.2, smallest blast radius), so
 * the frontends send 0 rather than omitting the key.
 *
 * Creates only — AC19: the create path is the ONLY thing that may reference
 * this constant. Edits and pending-truck approvals pass the amount already on
 * the record through unchanged — fleet mode hides the number, it never deletes
 * it, or AC5's "your per-truck amounts will be used again" would be a lie
 * (John, 2026-08-25).
 */
export const FLEET_MODE_ASSET_INSURANCE_PAYMENT = 0;

/**
 * The customer's mode, defaulting to per-asset. Anything that is not an
 * explicit `fleet` — a vendor/partner record, a customer fetched before the
 * backend migration shipped, an unrecognized string — keeps today's behavior.
 */
export const resolveInsurancePremiumMode = (
  user: User | null | undefined
): InsurancePremiumMode =>
  user?.userType === Entity.Customer &&
  user?.insurance_premium_mode === InsurancePremiumMode.Fleet
    ? InsurancePremiumMode.Fleet
    : InsurancePremiumMode.PerAsset;

/** Stored fleet premium in cents, or null when the customer has never set one. */
export const resolveFleetAmountCents = (
  user: User | null | undefined
): number | null => {
  if (user?.userType !== Entity.Customer) return null;
  const cents = user?.fleet_insurance_premium_amount_cents;
  return typeof cents === 'number' ? cents : null;
};

/** Cents → the dollar value the input binds to. */
export const centsToDollars = (
  cents: number | null | undefined
): number | null => (typeof cents === 'number' ? cents / 100 : null);

/**
 * Dollars typed by the customer → integer cents for the API.
 *
 * The rounding is `parseDollarsToCents`' (attributionUtils), so there is one
 * dollars→cents rule in the app rather than two that could drift (M6). This
 * wrapper adds only what the insurance domain needs on top: a `number | null`
 * input rather than a string, and a floor at 0 — a premium cannot be negative,
 * whereas attribution amounts legitimately can.
 */
export const dollarsToCents = (dollars: number | null | undefined): number =>
  typeof dollars === 'number' && Number.isFinite(dollars)
    ? Math.max(0, parseDollarsToCents(String(dollars)))
    : 0;

/**
 * Sum of the per-asset monthly premiums, in cents — what the budget goes back
 * to when the customer switches out of fleet mode. Amounts are dollars on the
 * asset records, so each one is converted before summing to keep the total
 * exact.
 */
export const sumAssetInsuranceCents = (assets: Asset[] | undefined): number =>
  (assets ?? []).reduce(
    (total, asset) =>
      total + dollarsToCents(Number((asset as any)?.insurance_payment_amount)),
    0
  );

/**
 * Spec §3.6 — shown the moment fleet mode is chosen, so "one number for the
 * fleet" is never ambiguous about trailers or about the per-truck fields
 * disappearing.
 */
export const fleetModeExplainer = (amountCents: number): string =>
  `We'll budget ${formatCentsToDollars(amountCents)}/mo for insurance across your whole fleet (trucks and trailers). You won't enter insurance per truck.`;

/**
 * Spec §3.6 — shown when switching fleet → per-truck. The per-asset amounts
 * were never destroyed, so the warning states what resumes and what it sums to.
 */
export const perAssetSwitchWarning = (perAssetSumCents: number): string =>
  `Your per-truck insurance amounts will be used again — they currently add up to ${formatCentsToDollars(perAssetSumCents)}/mo across your trucks and trailers.`;

/**
 * Collapsed-UI spec §2 — the one-line steady state; both modes now carry a
 * monthly figure (§7/R2.4). Fleet mode shows the single number the customer
 * typed ("Insurance: Entire Fleet · $2,000.00/mo"); Each Truck shows what the
 * per-asset amounts add up to, suffixed "total" so a computed sum never reads
 * as something someone entered. Dollars to the viewer, cents only on the wire,
 * same as everywhere else in this file.
 */
export const fleetInsuranceSummary = (
  mode: InsurancePremiumMode,
  fleetAmountCents: number | null | undefined,
  perAssetSumCents: number,
  perAssetSumPending = false
): string => {
  if (mode === InsurancePremiumMode.Fleet) {
    // The fleet figure is the customer's own number off the user record — it
    // owes nothing to the asset fetch, so it is never withheld.
    return `${INSURANCE_SUMMARY_LABEL}: ${FLEET_OPTION_LABEL} · ${formatCentsToDollars(
      fleetAmountCents ?? 0
    )}/mo`;
  }
  // Each Truck's figure is summed from the assets. Until they land, that sum is
  // 0 — and "$0.00/mo total" is a claim about the customer's money, not a
  // loading state. Show the mode alone and add the total once it is real (W3,
  // John 2026-08-25).
  if (perAssetSumPending) {
    return `${INSURANCE_SUMMARY_LABEL}: ${PER_ASSET_OPTION_LABEL}`;
  }
  return `${INSURANCE_SUMMARY_LABEL}: ${PER_ASSET_OPTION_LABEL} · ${formatCentsToDollars(
    Number.isFinite(perAssetSumCents) ? perAssetSumCents : 0
  )}/mo total`;
};
